import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiFileText, FiSearch, FiAlertCircle, FiCheck } from 'react-icons/fi';
import './BaakTagihan.css';

const BaakTagihan = () => {
  const [tagihanList, setTagihanList] = useState([]);
  const [mahasiswaList, setMahasiswaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    nim: '',
    jenis_tagihan: '',
    semester: '',
    tahun_ajaran: '',
    jumlah: '',
    deadline: '',
    status: 'belum_lunas',
    keterangan: ''
  });
  const [selectedTagihan, setSelectedTagihan] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSemester, setFilterSemester] = useState('');
  const [searchNim, setSearchNim] = useState('');
  
  // State untuk searchable mahasiswa dropdown
  const [mahasiswaSearch, setMahasiswaSearch] = useState('');
  const [showMahasiswaDropdown, setShowMahasiswaDropdown] = useState(false);
  const [selectedMahasiswa, setSelectedMahasiswa] = useState(null);

  const jenisTagihanOptions = [
    'SPP',
    'Biaya Pendaftaran',
    'Biaya Praktikum',
    'UTS',
    'UAS',
    'Skripsi',
    'Wisuda',
    'Herregistrasi',
    'Lainnya'
  ];

  const semesterOptions = [
    'Ganjil',
    'Genap'
  ];

  const statusOptions = [
    { value: 'belum_lunas', label: 'Belum Lunas' },
    { value: 'lunas', label: 'Lunas' },
    { value: 'terlambat', label: 'Terlambat' }
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tagihanRes, mahasiswaRes] = await Promise.all([
        axios.get('/api/tagihan'),
        axios.get('/api/students', { params: { limit: 10000 } })
      ]);
      
      setTagihanList(tagihanRes.data.data || []);
      setMahasiswaList(mahasiswaRes.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(error.response?.data?.message || 'Gagal mengambil data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle click outside untuk close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMahasiswaDropdown && !event.target.closest('.searchable-select')) {
        setShowMahasiswaDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMahasiswaDropdown]);

  const handleOpenModal = (tagihan = null) => {
    console.log('handleOpenModal called', { tagihan, showModal });
    
    if (tagihan) {
      setEditMode(true);
      setSelectedTagihan(tagihan);
      setFormData({
        nim: tagihan.nim,
        jenis_tagihan: tagihan.jenis_tagihan,
        semester: tagihan.semester,
        tahun_ajaran: tagihan.tahun_ajaran,
        jumlah: tagihan.jumlah,
        deadline: tagihan.deadline.split('T')[0],
        status: tagihan.status,
        keterangan: tagihan.keterangan || ''
      });
      // Set selected mahasiswa untuk edit mode
      const mhs = mahasiswaList.find(m => m.nim === tagihan.nim);
      if (mhs) {
        setSelectedMahasiswa(mhs);
        setMahasiswaSearch(`${mhs.nim} - ${mhs.name}`);
      }
    } else {
      setEditMode(false);
      setSelectedTagihan(null);
      setFormData({
        nim: '',
        jenis_tagihan: '',
        semester: '',
        tahun_ajaran: new Date().getFullYear() + '/' + (new Date().getFullYear() + 1),
        jumlah: '',
        deadline: '',
        status: 'belum_lunas',
        keterangan: ''
      });
      setSelectedMahasiswa(null);
      setMahasiswaSearch('');
    }
    setShowModal(true);
    setShowMahasiswaDropdown(false);
    console.log('Modal should be shown now');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedTagihan(null);
    setMahasiswaSearch('');
    setSelectedMahasiswa(null);
    setShowMahasiswaDropdown(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search input untuk mahasiswa
  const handleMahasiswaSearchChange = (e) => {
    const value = e.target.value;
    setMahasiswaSearch(value);
    setShowMahasiswaDropdown(true);
    
    if (selectedMahasiswa) {
      setSelectedMahasiswa(null);
      setFormData(prev => ({ ...prev, nim: '' }));
    }
  };

  // Filter mahasiswa berdasarkan search
  const filteredMahasiswaOptions = mahasiswaList
    .filter(mhs => {
      if (!mahasiswaSearch || mahasiswaSearch.trim() === '') {
        return true;
      }
      const searchLower = mahasiswaSearch.toLowerCase();
      return (
        mhs.nim?.toLowerCase().includes(searchLower) ||
        mhs.name?.toLowerCase().includes(searchLower) ||
        mhs.program?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => a.nim && b.nim ? a.nim.localeCompare(b.nim) : 0);

  // Handle select mahasiswa dari dropdown
  const handleSelectMahasiswa = (mahasiswa) => {
    setSelectedMahasiswa(mahasiswa);
    setMahasiswaSearch(`${mahasiswa.nim} - ${mahasiswa.name}`);
    setFormData(prev => ({ ...prev, nim: mahasiswa.nim }));
    setShowMahasiswaDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nim || !formData.jenis_tagihan || !formData.jumlah || !formData.deadline) {
      toast.error('Mohon lengkapi data yang wajib diisi');
      return;
    }

    try {
      if (editMode) {
        await axios.put(`/api/tagihan/${selectedTagihan._id}`, formData);
        toast.success('Tagihan berhasil diupdate');
      } else {
        await axios.post('/api/tagihan', formData);
        toast.success('Tagihan berhasil ditambahkan');
      }
      
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus tagihan ini?')) {
      try {
        await axios.delete(`/api/tagihan/${id}`);
        toast.success('Tagihan berhasil dihapus');
        fetchData();
      } catch (error) {
        toast.error('Gagal menghapus tagihan');
      }
    }
  };

  const handleMarkAsPaid = async (tagihan) => {
    if (window.confirm(`Tandai tagihan ${tagihan.jenis_tagihan} sebagai lunas?`)) {
      try {
        await axios.put(`/api/tagihan/${tagihan._id}`, { ...tagihan, status: 'lunas' });
        toast.success('Tagihan berhasil ditandai sebagai lunas');
        fetchData();
      } catch (error) {
        toast.error('Gagal mengupdate status tagihan');
      }
    }
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(angka);
  };

  const formatTanggal = (tanggal) => {
    return new Date(tanggal).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredTagihan = tagihanList.filter(tagihan => {
    const matchStatus = !filterStatus || tagihan.status === filterStatus;
    const matchSemester = !filterSemester || tagihan.semester === filterSemester;
    const matchNim = !searchNim || tagihan.nim.toLowerCase().includes(searchNim.toLowerCase());
    return matchStatus && matchSemester && matchNim;
  });

  const totalTagihan = filteredTagihan.length;
  const totalLunas = filteredTagihan.filter(t => t.status === 'lunas').length;
  const totalBelumLunas = filteredTagihan.filter(t => t.status === 'belum_lunas').length;
  const totalTerlambat = filteredTagihan.filter(t => 
    t.status === 'belum_lunas' && new Date(t.deadline) < new Date()
  ).length;
  const totalNominal = filteredTagihan.reduce((sum, t) => sum + t.jumlah, 0);
  const totalTunggakan = filteredTagihan
    .filter(t => t.status === 'belum_lunas')
    .reduce((sum, t) => sum + t.jumlah, 0);

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="baak-tagihan-page">
      {/* Header */}
      <div className="modern-header">
        <div className="header-background">
          <div className="header-pattern"></div>
        </div>
        <div className="header-content">
          <div className="header-text">
            <div className="header-icon-wrapper">
              <FiFileText className="header-icon" />
            </div>
            <div>
              <h1>Manajemen Tagihan Mahasiswa</h1>
              <p>Kelola tagihan dan pembayaran mahasiswa</p>
            </div>
          </div>
          <div className="header-actions">
            <button 
              type="button"
              className="btn btn-glass" 
              onClick={() => {
                console.log('Button clicked!');
                handleOpenModal();
              }}
            >
              <FiPlus /> Tambah Tagihan
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <FiFileText />
          </div>
          <div className="stat-details">
            <h3>{totalTagihan}</h3>
            <p>Total Tagihan</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <FiCheck />
          </div>
          <div className="stat-details">
            <h3>{totalLunas}</h3>
            <p>Tagihan Lunas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
            <FiAlertCircle />
          </div>
          <div className="stat-details">
            <h3>{totalBelumLunas}</h3>
            <p>Belum Lunas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
            <FiAlertCircle />
          </div>
          <div className="stat-details">
            <h3>{totalTerlambat}</h3>
            <p>Terlambat</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card summary-primary">
          <div className="summary-label">Total Nominal</div>
          <div className="summary-value">{formatRupiah(totalNominal)}</div>
        </div>
        <div className="summary-card summary-danger">
          <div className="summary-label">Total Tunggakan</div>
          <div className="summary-value">{formatRupiah(totalTunggakan)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="filters">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Cari NIM..."
              value={searchNim}
              onChange={(e) => setSearchNim(e.target.value)}
            />
          </div>
          <div className="search-box">
            <select 
              className="filter-select"
              value={filterSemester}
              onChange={(e) => setFilterSemester(e.target.value)}
            >
              <option value="">Semua Semester</option>
              {semesterOptions.map(semester => (
                <option key={semester} value={semester}>{semester}</option>
              ))}
            </select>
          </div>
          <div className="search-box">
            <select 
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">Semua Status</option>
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="modern-table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th>No</th>
                <th>NIM</th>
                <th>Nama Mahasiswa</th>
                <th>Jenis Tagihan</th>
                <th>Semester</th>
                <th>Tahun Ajaran</th>
                <th>Jumlah</th>
                <th>Deadline</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredTagihan.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                    Tidak ada data tagihan
                  </td>
                </tr>
              ) : (
                filteredTagihan.map((tagihan, index) => {
                  const mahasiswa = mahasiswaList.find(m => m.nim === tagihan.nim);
                  const isOverdue = tagihan.status === 'belum_lunas' && new Date(tagihan.deadline) < new Date();
                  
                  return (
                    <tr key={tagihan._id} className={isOverdue ? 'row-overdue' : ''}>
                      <td>{index + 1}</td>
                      <td><strong>{tagihan.nim}</strong></td>
                      <td>{mahasiswa?.name || '-'}</td>
                      <td>{tagihan.jenis_tagihan}</td>
                      <td>{tagihan.semester}</td>
                      <td>{tagihan.tahun_ajaran}</td>
                      <td><strong>{formatRupiah(tagihan.jumlah)}</strong></td>
                      <td>{formatTanggal(tagihan.deadline)}</td>
                      <td>
                        <span className={`badge ${
                          tagihan.status === 'lunas' ? 'badge-success' :
                          isOverdue ? 'badge-danger' :
                          'badge-warning'
                        }`}>
                          {tagihan.status === 'lunas' ? 'Lunas' : isOverdue ? 'Terlambat' : 'Belum Lunas'}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          {tagihan.status === 'belum_lunas' && (
                            <button
                              className="table-btn table-btn-success"
                              onClick={() => handleMarkAsPaid(tagihan)}
                              title="Tandai Lunas"
                            >
                              <FiCheck />
                            </button>
                          )}
                          <button
                            className="table-btn table-btn-edit"
                            onClick={() => handleOpenModal(tagihan)}
                            title="Edit"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            className="table-btn table-btn-delete"
                            onClick={() => handleDelete(tagihan._id)}
                            title="Hapus"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      {console.log('Render check - showModal:', showModal)}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Tagihan' : 'Tambah Tagihan Baru'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Mahasiswa <span className="required">*</span></label>
                    <div className="searchable-select">
                      <div className="search-input-wrapper">
                        <FiSearch className="search-icon" />
                        <input
                          type="text"
                          value={mahasiswaSearch}
                          onChange={handleMahasiswaSearchChange}
                          onFocus={() => setShowMahasiswaDropdown(true)}
                          placeholder="Cari berdasarkan NIM atau Nama..."
                          className="form-control search-input"
                          autoComplete="off"
                          required={!selectedMahasiswa}
                          disabled={editMode}
                        />
                      </div>
                      {showMahasiswaDropdown && !editMode && (
                        <div className="searchable-dropdown">
                          {filteredMahasiswaOptions.length > 0 ? (
                            <>
                              <div className="dropdown-header">
                                {mahasiswaSearch 
                                  ? `Ditemukan ${filteredMahasiswaOptions.length} mahasiswa`
                                  : `Total ${filteredMahasiswaOptions.length} mahasiswa`
                                }
                              </div>
                              {filteredMahasiswaOptions.map(mhs => (
                                <div
                                  key={mhs._id}
                                  className="dropdown-item"
                                  onClick={() => handleSelectMahasiswa(mhs)}
                                >
                                  <div className="dropdown-item-main">
                                    <strong>{mhs.nim}</strong> - {mhs.name}
                                  </div>
                                  <div className="dropdown-item-sub">
                                    <span className="program-badge">{mhs.program}</span>
                                  </div>
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="dropdown-item-empty">
                              Mahasiswa tidak ditemukan
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Jenis Tagihan <span className="required">*</span></label>
                    <select
                      name="jenis_tagihan"
                      value={formData.jenis_tagihan}
                      onChange={handleChange}
                      className="form-control"
                      required
                    >
                      <option value="">Pilih Jenis Tagihan</option>
                      {jenisTagihanOptions.map(jenis => (
                        <option key={jenis} value={jenis}>{jenis}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Semester <span className="required">*</span></label>
                    <select
                      name="semester"
                      value={formData.semester}
                      onChange={handleChange}
                      className="form-control"
                      required
                    >
                      <option value="">Pilih Semester</option>
                      {semesterOptions.map(semester => (
                        <option key={semester} value={semester}>{semester}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Tahun Ajaran <span className="required">*</span></label>
                    <input
                      type="text"
                      name="tahun_ajaran"
                      value={formData.tahun_ajaran}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="2024/2025"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Jumlah Tagihan <span className="required">*</span></label>
                    <input
                      type="number"
                      name="jumlah"
                      value={formData.jumlah}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Nominal tagihan"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Deadline <span className="required">*</span></label>
                    <input
                      type="date"
                      name="deadline"
                      value={formData.deadline}
                      onChange={handleChange}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Keterangan</label>
                    <textarea
                      name="keterangan"
                      value={formData.keterangan}
                      onChange={handleChange}
                      className="form-control"
                      rows="2"
                      placeholder="Keterangan tambahan (opsional)"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FiX /> Batal
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiSave /> {editMode ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaakTagihan;